import { NewActionMenu } from "@/features/actions";
import { useGoToIntegrations } from "@/features/connectors";
import { SalesWorkArea } from "@/features/sales";
import { CreateCampaignDialog } from "@/features/sales/components/CreateCampaignDialog";
import { CreateLeadDialog } from "@/features/sales/components/CreateLeadDialog";
import { useCrmViewStore } from "@/features/sales/state/crmViewStore";
import { validateSalesSearch } from "@/features/workspace-router";
import { SubTabBar, useActiveSubTab } from "@/features/workspace/components/SubTabBar";
import { useSearchNavigation } from "@/features/workspace/hooks/useSearchNavigation";
import {
  SegmentedTabs,
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceLayout,
  WorkspaceSearchBar,
} from "@ando/ui";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Megaphone, Settings2, TrendingUp, UserPlus } from "lucide-react";
import * as React from "react";

export const Route = createFileRoute("/_authed/workspace/sales")({
  validateSearch: validateSalesSearch,
  component: SalesHub,
});

/**
 * Sales hub — CRM vertical (lead · clienti · opportunità · pipeline).
 *
 * Jun 2026 — split out of the cross-cutting "Lavoro" tab so CRM has a home.
 * Lean by design for v1: header + "+ Nuovo" manual-action menu (Nuovo lead /
 * cliente / opportunità, provider-aware via the unified action flow) +
 * proactive briefing + empty state. Pipeline/list surfaces land once a CRM
 * connector feeds data. AI proposals route here automatically
 * (``salesforce_*`` / ``hubspot_*`` → ``resolveTabForActionType`` → sales).
 */
// Every known sub-tab now has a native panel (prospecting + CRM spine), so the
// work-area handles them all; unknown/legacy values fall through to overview.
const KNOWN_SUBTABS = new Set([
  "overview",
  "pipeline",
  "contacts",
  "companies",
  "campaigns",
  "leads",
  "suppression",
]);

// Sub-tabs backed by a searchable list/board → the command bar shows the
// shared search + the panel's summary for these; the dashboard/overview tabs
// have their own affordances so they omit both.
const SEARCHABLE_SUBTABS = new Set(["pipeline", "contacts", "companies"]);

const SALES_SEARCH_PLACEHOLDER: Record<string, MessageDescriptor> = {
  pipeline: msg`Cerca opportunità…`,
  contacts: msg`Cerca contatti…`,
  companies: msg`Cerca aziende…`,
};

function SalesHub() {
  const { i18n } = useLingui();
  const goToIntegrations = useGoToIntegrations();
  const subTab = useActiveSubTab("sales");
  const isKnown = KNOWN_SUBTABS.has(subTab);
  const searchable = SEARCHABLE_SUBTABS.has(subTab);

  const search = useSearch({ from: "/_authed/workspace/sales" });
  const searchNav = useSearchNavigation<Record<string, never>>("/workspace/sales");
  // The active data panel reports its own headline node here ("N contatti",
  // "aperta {€}"…). Only shown on a ``searchable`` tab, and the mounting panel
  // overwrites it immediately, so a value from a previous tab can never surface.
  const [summary, setSummary] = React.useState<React.ReactNode>(null);

  // Pipeline scope (Tutte/Mie/Ferme) — its segmented control renders in the
  // command bar ``range`` zone; the board reads the same store to filter.
  const boardScope = useCrmViewStore((s) => s.boardScope);
  const setBoardScope = useCrmViewStore((s) => s.setBoardScope);

  const [campaignDialogOpen, setCampaignDialogOpen] = React.useState(false);
  const [leadDialogOpen, setLeadDialogOpen] = React.useState(false);

  return (
    <SurfaceLayout
      header={
        // Jul 2026 — single-row command bar (replaces stacked header + hero +
        // toolbar rows). The view switcher (Overview · Pipeline · Contatti …)
        // is PRIMARY navigation → it lives in the always-visible ``views`` slot,
        // never buried in a filter popover.
        <SurfaceCommandBar
          views={<SubTabBar tabId="sales" className="!px-0 !py-0" />}
          count={searchable ? summary : undefined}
          search={
            searchable ? (
              <WorkspaceSearchBar
                placeholder={i18n._(SALES_SEARCH_PLACEHOLDER[subTab] ?? msg`Cerca…`)}
                ariaLabel={i18n._(msg`Cerca nel CRM`)}
                initialQuery={search.q ?? ""}
                onSearch={searchNav.setQuery}
              />
            ) : undefined
          }
          range={
            subTab === "pipeline" ? (
              <SegmentedTabs
                ariaLabel={i18n._(msg`Ambito pipeline`)}
                value={boardScope}
                onChange={setBoardScope}
                options={[
                  { value: "all", label: i18n._(msg`Tutte`) },
                  { value: "mine", label: i18n._(msg`Mie`) },
                  { value: "stale", label: i18n._(msg`Ferme`) },
                ]}
              />
            ) : undefined
          }
          actions={
            <NewActionMenu
              area="sales"
              label={i18n._(msg`Nuovo`)}
              extraItems={[
                {
                  id: "sales:new_campaign",
                  label: i18n._(msg`Nuova campagna`),
                  icon: Megaphone,
                  onSelect: () => setCampaignDialogOpen(true),
                },
                {
                  id: "sales:new_lead",
                  label: i18n._(msg`Nuovo lead`),
                  icon: UserPlus,
                  onSelect: () => setLeadDialogOpen(true),
                },
                {
                  id: "sales:configure_connectors",
                  label: i18n._(msg`Configura connettori`),
                  icon: Settings2,
                  onSelect: () => goToIntegrations("sales"),
                },
              ]}
            />
          }
        />
      }
    >
      {isKnown ? (
        <SalesWorkArea query={search.q ?? ""} onSummary={setSummary} />
      ) : (
        <SurfaceEmptyState
          icon={<TrendingUp size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Sezione non disponibile`)}
          description={i18n._(
            msg`Questa sezione non esiste più — usa Pipeline, Contatti o Aziende qui sopra.`,
          )}
        />
      )}

      {/* Native-CRM create primitives, surfaced from the "+ Nuovo" menu. */}
      <CreateCampaignDialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen} />
      <CreateLeadDialog open={leadDialogOpen} onOpenChange={setLeadDialogOpen} />
    </SurfaceLayout>
  );
}
